/**
 * Searchandising hooks using TanStack Query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// Types
export type RuleType = 'boost' | 'bury' | 'pin' | 'exclude'
export type QueryMatchType = 'exact' | 'contains' | 'regex' | 'all'

export interface SearchandisingRule {
  id: string
  name: string
  type: RuleType
  queryMatch: string | null
  queryMatchType: string | null
  productIds: string | null // JSON array
  categoryIds: string | null // JSON array
  tagMatch: string | null
  priceMin: number | null
  priceMax: number | null
  onSale: number | null
  inStock: number | null
  boostAmount: number | null
  position: number | null
  startDate: number | null
  endDate: number | null
  enabled: number | null
  priority: number | null
  hitCount: number | null
  lastHitAt: number | null
  createdAt: number
  updatedAt: number
}

export interface CreateRuleInput {
  name: string
  type: RuleType
  queryMatch?: string
  queryMatchType?: QueryMatchType
  productIds?: string[]
  categoryIds?: string[]
  tagMatch?: string
  priceMin?: number
  priceMax?: number
  onSale?: boolean
  inStock?: boolean
  boostAmount?: number
  position?: number
  startDate?: number
  endDate?: number
  enabled?: boolean
  priority?: number
}

export interface UpdateRuleInput {
  name?: string
  type?: RuleType
  queryMatch?: string | null
  queryMatchType?: QueryMatchType
  productIds?: string[] | null
  categoryIds?: string[] | null
  tagMatch?: string | null
  priceMin?: number | null
  priceMax?: number | null
  onSale?: boolean | null
  inStock?: boolean | null
  boostAmount?: number
  position?: number | null
  startDate?: number | null
  endDate?: number | null
  enabled?: boolean
  priority?: number
}

export interface PreviewProduct {
  id: string
  title: string
  price: number | null
  imageUrl: string | null
  category: string | null
}

// Fetch all rules
export function useSearchandisingRules() {
  return useQuery({
    queryKey: ['searchandising'],
    queryFn: async (): Promise<SearchandisingRule[]> => {
      const res = await fetch('/api/searchandising', { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch rules')
      const data = (await res.json()) as { rules: SearchandisingRule[] }
      return data.rules
    },
  })
}

// Get single rule
export function useSearchandisingRule(id: string | null) {
  return useQuery({
    queryKey: ['searchandising', id],
    enabled: !!id,
    queryFn: async (): Promise<SearchandisingRule | null> => {
      if (!id) return null
      const res = await fetch(`/api/searchandising/${id}`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch rule')
      const data = (await res.json()) as { rule: SearchandisingRule }
      return data.rule
    },
  })
}

// Create rule
export function useCreateRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: CreateRuleInput): Promise<SearchandisingRule> => {
      const res = await fetch('/api/searchandising', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      if (!res.ok) throw new Error('Failed to create rule')
      const data = (await res.json()) as { rule: SearchandisingRule }
      return data.rule
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchandising'] })
    },
  })
}

// Update rule
export function useUpdateRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: UpdateRuleInput & { id: string }): Promise<SearchandisingRule> => {
      const res = await fetch(`/api/searchandising/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      if (!res.ok) throw new Error('Failed to update rule')
      const data = (await res.json()) as { rule: SearchandisingRule }
      return data.rule
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchandising'] })
    },
  })
}

// Delete rule
export function useDeleteRule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`/api/searchandising/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to delete rule')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['searchandising'] })
    },
  })
}

// Preview rule effect
export function usePreviewRule() {
  return useMutation({
    mutationFn: async (
      input: CreateRuleInput
    ): Promise<{ totalAffected: number; preview: PreviewProduct[] }> => {
      const res = await fetch('/api/searchandising/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(input),
      })
      if (!res.ok) throw new Error('Failed to preview rule')
      return res.json() as Promise<{ totalAffected: number; preview: PreviewProduct[] }>
    },
  })
}

// Helper to parse JSON arrays from rule
export function parseRuleArray(json: string | null): string[] {
  if (!json) return []
  try {
    return JSON.parse(json) as string[]
  } catch {
    return []
  }
}

// Helper to format rule type for display
export function formatRuleType(type: RuleType): string {
  switch (type) {
    case 'boost':
      return 'Boost'
    case 'bury':
      return 'Bury'
    case 'pin':
      return 'Pin to Position'
    case 'exclude':
      return 'Exclude'
    default:
      return type
  }
}

// Helper to get rule type color
export function getRuleTypeColor(type: RuleType): string {
  switch (type) {
    case 'boost':
      return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/30'
    case 'bury':
      return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30'
    case 'pin':
      return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
    case 'exclude':
      return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
    default:
      return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/30'
  }
}
